import Header from '../components/Header';
import Footer from '../components/Footer';
import { Heart, AlertTriangle, Shield } from 'lucide-react';

const ResponsibleGaming = () => {
  return (
    <div className="min-h-screen bg-dark-bg">
      <Header />
      <main className="py-20">
        <div className="container mx-auto px-4 max-w-4xl">
          <article className="bg-dark-card border-2 border-gray-800 rounded-2xl p-8 md:p-12">
            <div className="flex items-center mb-6">
              <Heart className="w-8 h-8 text-toxic-green mr-4" />
              <h1 className="text-4xl md:text-5xl font-bold text-white">
                Jeu Responsable
              </h1>
            </div>

            <div className="prose prose-invert max-w-none">
              <p className="text-gray-300 mb-8 leading-relaxed text-lg">
                Chez Hexabet Connect, nous croyons fermement que les paris en ligne doivent rester un loisir agréable et contrôlé. Nous nous engageons à promouvoir le jeu responsable et à fournir des informations et des ressources pour vous aider à maintenir une relation saine avec les paris en ligne.
              </p>

              <h2 className="text-3xl font-bold mt-12 mb-6 text-white">
                Qu'est-ce que le Jeu Responsable ?
              </h2>
              <p className="text-gray-300 mb-6 leading-relaxed">
                Le jeu responsable consiste à parier de manière contrôlée, en fixant des limites de temps et d'argent, et en reconnaissant que les paris doivent rester un divertissement, pas une source de revenus ou un moyen de résoudre des problèmes financiers. C'est parier pour le plaisir, pas par nécessité ou compulsion.
              </p>
              <p className="text-gray-300 mb-6 leading-relaxed">
                Les paris en ligne peuvent être amusants et excitants, mais ils comportent aussi des risques. Il est essentiel de comprendre ces risques et de prendre des mesures pour les gérer. Le jeu responsable, c'est reconnaître ces risques et agir en conséquence.
              </p>

              <h2 className="text-3xl font-bold mt-12 mb-6 text-white">
                Principes du Jeu Responsable
              </h2>
              <h3 className="text-2xl font-bold mt-8 mb-4 text-white">
                1. Ne Pariez Que Ce Que Vous Pouvez Vous Permettre de Perdre
              </h3>
              <p className="text-gray-300 mb-6 leading-relaxed">
                C'est le principe le plus important. Ne jamais parier avec de l'argent dont vous avez besoin pour vivre, pour payer vos factures, ou pour épargner. Les paris doivent être financés par un budget de loisirs, pas par vos finances essentielles.
              </p>
              <p className="text-gray-300 mb-6 leading-relaxed">
                Fixez un budget mensuel pour les paris et respectez-le strictement. Ne dépassez jamais ce budget, même si vous avez gagné et que vous voulez "profiter". Les gains peuvent être retirés et utilisés pour autre chose, pas nécessairement pour parier à nouveau.
              </p>

              <h3 className="text-2xl font-bold mt-8 mb-4 text-white">
                2. Fixez des Limites de Temps
              </h3>
              <p className="text-gray-300 mb-6 leading-relaxed">
                Les paris peuvent être chronophages, surtout le live betting. Fixez des limites de temps pour vos sessions de paris et respectez-les. Ayez une vie en dehors des paris : travail, famille, hobbies, amis. Les paris ne doivent pas devenir votre seule activité.
              </p>
              <p className="text-gray-300 mb-6 leading-relaxed">
                Utilisez les outils de limitation de temps proposés par les plateformes. Beaucoup de plateformes agréées permettent de fixer des limites de temps quotidiennes, hebdomadaires, ou mensuelles. Utilisez ces outils pour vous protéger.
              </p>

              <h3 className="text-2xl font-bold mt-8 mb-4 text-white">
                3. Ne Pariez Pas Sous l'Influence
              </h3>
              <p className="text-gray-300 mb-6 leading-relaxed">
                Ne pariez jamais sous l'influence de l'alcool, de drogues, ou d'émotions fortes (colère, tristesse, excitation excessive). Ces états altèrent votre jugement et peuvent vous amener à prendre des décisions que vous regretterez.
              </p>
              <p className="text-gray-300 mb-6 leading-relaxed">
                Si vous vous sentez émotionnellement instable ou si vous avez consommé de l'alcool, évitez de parier. Attendez d'être dans un état d'esprit clair et équilibré avant de prendre des décisions de paris.
              </p>

              <h3 className="text-2xl font-bold mt-8 mb-4 text-white">
                4. Ne Cherchez Pas à "Rattraper" vos Pertes
              </h3>
              <p className="text-gray-300 mb-6 leading-relaxed">
                Après une perte, l'envie de "rattraper" en pariant plus est forte. Résistez à cette tentation. Augmenter vos mises après une perte est la meilleure façon de tout perdre rapidement. Les pertes font partie des paris - acceptez-les et respectez votre budget.
              </p>
              <p className="text-gray-300 mb-6 leading-relaxed">
                Si vous avez perdu votre budget mensuel, arrêtez pour le mois. Ne déposez pas plus pour "rattraper". Attendez le mois suivant et repartez avec votre budget normal.
              </p>

              <h3 className="text-2xl font-bold mt-8 mb-4 text-white">
                5. Les Paris Ne Sont Pas un Investissement
              </h3>
              <p className="text-gray-300 mb-6 leading-relaxed">
                Les paris en ligne ne sont pas un investissement. Ils ne sont pas un moyen de gagner de l'argent de manière fiable. Sur le long terme, la plupart des parieurs perdent de l'argent. C'est la réalité, et l'accepter est essentiel.
              </p>
              <p className="text-gray-300 mb-6 leading-relaxed">
                Si vous pariez, considérez l'argent que vous misez comme le coût d'un loisir, pas comme un investissement. Vous payez pour le divertissement et l'excitation, pas pour gagner de l'argent.
              </p>

              <h2 className="text-3xl font-bold mt-12 mb-6 text-white">
                Signes d'Alerte
              </h2>
              <p className="text-gray-300 mb-6 leading-relaxed">
                Reconnaître les signes d'un problème potentiel est essentiel. Si vous reconnaissez ces signes chez vous, il est temps de prendre des mesures :
              </p>
              <ul className="list-disc list-inside space-y-3 text-gray-300 mb-6 ml-4">
                <li>Vous pariez plus que vous ne pouvez vous le permettre</li>
                <li>Vous mentez à votre entourage sur vos paris ou vos pertes</li>
                <li>Vous pariez pour "rattraper" des pertes</li>
                <li>Les paris affectent votre travail, vos études, ou vos relations</li>
                <li>Vous vous sentez anxieux, déprimé, ou coupable à cause des paris</li>
                <li>Vous pensez constamment aux paris</li>
                <li>Vous avez besoin de parier des montants de plus en plus importants pour ressentir la même excitation</li>
                <li>Vous avez des difficultés à arrêter de parier</li>
                <li>Vous négligez d'autres activités importantes pour parier</li>
                <li>Vous empruntez de l'argent pour parier</li>
                <li>Vous vous sentez obligé de parier même quand vous ne le voulez pas</li>
              </ul>
              <p className="text-gray-300 mb-6 leading-relaxed">
                Si vous reconnaissez plusieurs de ces signes, il est important d'agir. Plus tôt vous agissez, plus facile c'est de retrouver le contrôle.
              </p>

              <h2 className="text-3xl font-bold mt-12 mb-6 text-white">
                Outils de Contrôle Disponibles
              </h2>
              <h3 className="text-2xl font-bold mt-8 mb-4 text-white">
                Limites de Dépôt
              </h3>
              <p className="text-gray-300 mb-6 leading-relaxed">
                Toutes les plateformes agréées permettent de fixer des limites de dépôt quotidiennes, hebdomadaires, ou mensuelles. Une fois fixées, ces limites ne peuvent généralement être augmentées qu'après un délai de réflexion (24 à 48 heures), ce qui vous protège contre les décisions impulsives.
              </p>

              <h3 className="text-2xl font-bold mt-8 mb-4 text-white">
                Limites de Mise
              </h3>
              <p className="text-gray-300 mb-6 leading-relaxed">
                Vous pouvez aussi fixer des limites sur le montant que vous pouvez miser par jour, semaine, ou mois. Ces limites vous aident à contrôler vos dépenses et à rester dans votre budget.
              </p>

              <h3 className="text-2xl font-bold mt-8 mb-4 text-white">
                Limites de Temps
              </h3>
              <p className="text-gray-300 mb-6 leading-relaxed">
                Beaucoup de plateformes permettent de fixer des limites de temps pour vos sessions de paris. Une fois la limite atteinte, vous ne pouvez plus parier jusqu'à la période suivante.
              </p>

              <h3 className="text-2xl font-bold mt-8 mb-4 text-white">
                Auto-Exclusion
              </h3>
              <p className="text-gray-300 mb-6 leading-relaxed">
                Si vous sentez que vous perdez le contrôle, vous pouvez vous exclure temporairement (24 heures, 7 jours, 30 jours) ou définitivement. Pendant la période d'exclusion, vous ne pouvez pas accéder à votre compte, déposer, ou parier.
              </p>
              <p className="text-gray-300 mb-6 leading-relaxed">
                L'Autorité Nationale des Jeux (ANJ) offre aussi un système d'interdiction volontaire qui s'applique à toutes les plateformes agréées. Si vous vous inscrivez à ce système, vous ne pourrez pas créer de compte ou parier sur aucune plateforme agréée en France pendant au moins 3 ans.
              </p>

              <h2 className="text-3xl font-bold mt-12 mb-6 text-white">
                Où Trouver de l'Aide
              </h2>
              <p className="text-gray-300 mb-6 leading-relaxed">
                Si vous avez besoin d'aide, plusieurs ressources sont disponibles :
              </p>
              <div className="bg-toxic-green/10 border border-toxic-green/30 rounded-xl p-6 mt-6 mb-6">
                <h3 className="text-xl font-bold mb-4 text-white">Services d'Aide</h3>
                <ul className="space-y-3 text-gray-300">
                  <li><strong>Joueurs Info Service :</strong> 09 74 75 13 13 (service d'écoute gratuit et anonyme)</li>
                  <li><strong>SOS Joueurs :</strong> Accompagnement et soutien aux joueurs en difficulté (www.sosjoueurs.org)</li>
                  <li><strong>ANJ - Autorité Nationale des Jeux :</strong> Informations et ressources sur le jeu responsable (www.anj.fr)</li>
                  <li><strong>Évalujeu :</strong> Outil d'auto-évaluation pour identifier les risques (www.evalujeu.fr)</li>
                </ul>
              </div>
              <p className="text-gray-300 mb-6 leading-relaxed">
                N'hésitez pas à demander de l'aide. Les problèmes de jeu peuvent être traités, et plus tôt vous agissez, mieux c'est. Vous n'êtes pas seul, et il n'y a pas de honte à avoir besoin d'aide.
              </p>

              <h2 className="text-3xl font-bold mt-12 mb-6 text-white">
                Notre Engagement
              </h2>
              <p className="text-gray-300 mb-6 leading-relaxed">
                Chez Hexabet Connect, nous nous engageons à promouvoir le jeu responsable. Nous fournissons des informations sur les risques, les outils de contrôle disponibles, et les ressources d'aide. Nous encourageons toujours le jeu responsable dans nos contenus et nos communications.
              </p>
              <p className="text-gray-300 mb-6 leading-relaxed">
                Nous ne ciblons jamais les personnes vulnérables, et nous ne présentons jamais les paris comme un moyen de résoudre des problèmes financiers ou comme une source de revenus fiable. Les paris sont un loisir, et nous le présentons toujours comme tel.
              </p>

              <h2 className="text-3xl font-bold mt-12 mb-6 text-white">
                Rappel Important
              </h2>
              <div className="bg-red-500/10 border-2 border-red-500/30 rounded-xl p-6 mt-6">
                <p className="text-white mb-4 font-bold text-lg flex items-center">
                  <AlertTriangle className="w-6 h-6 text-red-500 mr-3" />
                  Les paris en ligne sont interdits aux mineurs de moins de 18 ans
                </p>
                <p className="text-gray-300">
                  Si vous avez moins de 18 ans, vous ne devez pas utiliser notre site ou créer un compte sur une plateforme de paris. Les plateformes agréées vérifient l'âge et refusent les mineurs.
                </p>
              </div>

              <h2 className="text-3xl font-bold mt-12 mb-6 text-white">
                Contact
              </h2>
              <p className="text-gray-300 mb-6 leading-relaxed">
                Pour toute question concernant le jeu responsable, contactez-nous :
              </p>
              <div className="bg-toxic-green/10 border border-toxic-green/30 rounded-xl p-6 mt-6">
                <p className="text-gray-300 mb-2"><strong>Email :</strong> info@hexabetconnect.com</p>
                <p className="text-gray-300 mb-2"><strong>Téléphone :</strong> +33 4 21 73 98 33</p>
                <p className="text-gray-300"><strong>Adresse :</strong> Paris, France</p>
              </div>
            </div>
          </article>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default ResponsibleGaming;

